import {
  getActiveProductLineId,
  getProductLineDeclarations,
} from '../../../adapters';
import { getShellState } from '../../../shell/store/shellStore';

// ---------------------------------------------------------------------------
// Attachment-enabled canvas registry
// ---------------------------------------------------------------------------

/** Ontology Builder module ukey used by the host route. */
export const ONTOLOGY_BUILDER_CANVAS_UKEY = 'daxl-ontology-builder';

/**
 * FSS `storage_bucket.ukey` used for canvas-level user-uploaded files.
 * Must match a row in the FSS `storage_bucket` table for the deployment.
 * "working-zone" is the active-working-files bucket seeded by default.
 */
export const UPLOAD_BUCKET_UKEY = 'working-zone';

/**
 * FSS inbound subpaths aligned with DKS dataset_mapping.ref_location
 * (minus the data_collector/inbound/ prefix FSS adds automatically).
 * Must match application-seed lens CSV→NDS paths under inbound/nds/...
 */
export const UPLOAD_SUBPATH_HOME_KITCHEN = 'nds/pim/home_kitchen/products';
export const UPLOAD_SUBPATH_POWER_TOOLS = 'nds/pim/power_tools/products';
export const UPLOAD_SUBPATH_AIR_FRYERS = 'nds/pim/air_fryers/products';
export const UPLOAD_SUBPATH_COFFEE_MAKERS = 'nds/pim/coffee_makers/products';
export const UPLOAD_SUBPATH_VACUUM_CLEANERS = 'nds/pim/vacuum_cleaners/products';
export const UPLOAD_SUBPATH_ART_AND_CRAFT_SUPPLIES =
  'nds/pim/art_and_craft_supplies/products';
export const UPLOAD_SUBPATH_BATH_AND_BODY = 'nds/pim/bath_and_body/products';
export const UPLOAD_SUBPATH_BEAUTY_TOOLS = 'nds/pim/beauty_tools/products';
export const UPLOAD_SUBPATH_HAND_BAGS = 'nds/pim/hand_bags/products';
export const UPLOAD_SUBPATH_HOME_DINING = 'nds/pim/home_dining/products';
export const UPLOAD_SUBPATH_SINGLE_BOARD_COMPUTERS =
  'nds/pim/single_board_computers/products';
export const UPLOAD_SUBPATH_WIRE_AND_CABLE = 'nds/pim/wire_and_cable/products';
export const UPLOAD_SUBPATH_ETHERNET_SWITCHES = 'nds/pim/ethernet_switches/products';
export const UPLOAD_SUBPATH_FLASHLIGHTS_AND_WORK_LIGHTS =
  'nds/pim/flashlights_and_work_lights/products';
export const UPLOAD_SUBPATH_BATTERIES = 'nds/pim/batteries/products';

/** Home-kitchen merged CSV lens — used only when a filename maps to HK. */
export const DEFAULT_UPLOAD_SUBPATH = UPLOAD_SUBPATH_HOME_KITCHEN;

/**
 * @deprecated Use resolveUploadSubpath() — kept for tests that assert the default.
 */
export const UPLOAD_SUBPATH = DEFAULT_UPLOAD_SUBPATH;

/**
 * Resolve the org/solution tenant for FSS uploads from shell auth or product-line BFF config.
 * Must match the tenant_ukey used by ESA converse / CDS invoke (e.g. brahe).
 */
export function resolveUploadTenantUkey(): string {
  const shellTenant = (getShellState()?.tenant?.id ?? '').trim();
  if (shellTenant) {
    return shellTenant;
  }
  try {
    const plId = getActiveProductLineId();
    const declarations = getProductLineDeclarations();
    const pl = declarations.find(d => d.id === plId) ?? declarations[0];
    return (pl?.bff.tenant ?? pl?.auth.tenant ?? '').trim();
  } catch {
    return '';
  }
}

/** Bucket ukey sent to FSS for canvas-attached file uploads. */
export const UPLOAD_BUCKET_UKEY_FOR_UPLOAD = 'raw-zone';

/**
 * Canvases for which the file attachment UI is shown.
 *
 * The Ontology Builder shell route uses the module ukey while capability tabs
 * activate concrete child canvas ukeys, so the gate accepts both forms.
 */
export const ATTACHMENT_ENABLED_CANVASES: readonly string[] = [
  ONTOLOGY_BUILDER_CANVAS_UKEY,
  'daxl-ontology-builder-dashboard',
  'daxl-ontology-builder-results',
  'daxl-ontology-builder-explorer',
  'daxl-ontology-builder-runs',
];

/**
 * Extract the canvas/module ukey from a host route path.
 *
 * On a full page reload the in-memory shell store is empty until the workspace
 * remote re-dispatches navigation events, so the URL is the only durable signal
 * of which canvas is loaded. Matches the segment after `/canvas/`, e.g.
 * `/operational/canvas/daxl-ontology-builder/explorer` → `daxl-ontology-builder`.
 */
export function extractCanvasUkeyFromPath(pathname: string): string | null {
  const match = pathname.match(/\/canvas\/([^/?#]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : null;
}

type UploadDomain =
  | 'home_kitchen'
  | 'power_tools'
  | 'air_fryers'
  | 'coffee_makers'
  | 'vacuum_cleaners'
  | 'art_and_craft_supplies'
  | 'bath_and_body'
  | 'beauty_tools'
  | 'hand_bags'
  | 'home_dining'
  | 'single_board_computers'
  | 'wire_and_cable'
  | 'ethernet_switches'
  | 'flashlights_and_work_lights'
  | 'batteries';

function inferUploadDomainFromFileName(fileName: string): UploadDomain | null {
  const normalized = fileName.toLowerCase();
  if (/_acs_|art[_-]?and[_-]?craft|art[_-]?craft[_-]?supplies?|sbd_acs/.test(normalized)) {
    return 'art_and_craft_supplies';
  }
  if (
    /_fwl_|flashlights?[_-]?and[_-]?work[_-]?lights?|work[_-]?lights?|flashlights?|sbd_fwl/.test(
      normalized,
    )
  ) {
    return 'flashlights_and_work_lights';
  }
  if (/_sbc_|single[_-]?board[_-]?computers?|sbd_sbc/.test(normalized)) {
    return 'single_board_computers';
  }
  if (/_esw_|ethernet[_-]?switches?|sbd_esw/.test(normalized)) {
    return 'ethernet_switches';
  }
  if (/_wac_|wire[_-]?and[_-]?cable|wire[_-]?cable|sbd_wac/.test(normalized)) {
    return 'wire_and_cable';
  }
  if (/_bb_|bath[_-]?and[_-]?body|bath[_-]?body|sbd_bb/.test(normalized)) {
    return 'bath_and_body';
  }
  if (/_bt_|beauty[_-]?tools|sbd_bt/.test(normalized)) {
    return 'beauty_tools';
  }
  if (/_hbg_|hand[_-]?bags?|sbd_hbg/.test(normalized)) {
    return 'hand_bags';
  }
  if (/_hd_|home[_-]?dining|sbd_hd/.test(normalized)) {
    return 'home_dining';
  }
  if (/_bat_|batteries|battery|sbd_bat/.test(normalized)) {
    return 'batteries';
  }
  if (/_cm_|coffee[_-]?makers?|sbd_cm/.test(normalized)) {
    return 'coffee_makers';
  }
  if (/_vc_|vacuum[_-]?cleaners?|sbd_vc/.test(normalized)) {
    return 'vacuum_cleaners';
  }
  if (/_af_|air[_-]?fryers?|sbd_af/.test(normalized)) {
    return 'air_fryers';
  }
  if (/_pt_|power[_-]?tools|sbd_pt/.test(normalized)) {
    return 'power_tools';
  }
  if (/_hk_|home[_-]?kitchen|sbd_hk/.test(normalized)) {
    return 'home_kitchen';
  }
  return null;
}

function uploadSubpathForDomain(domain: UploadDomain): string {
  switch (domain) {
    case 'home_kitchen':
      return UPLOAD_SUBPATH_HOME_KITCHEN;
    case 'power_tools':
      return UPLOAD_SUBPATH_POWER_TOOLS;
    case 'air_fryers':
      return UPLOAD_SUBPATH_AIR_FRYERS;
    case 'coffee_makers':
      return UPLOAD_SUBPATH_COFFEE_MAKERS;
    case 'vacuum_cleaners':
      return UPLOAD_SUBPATH_VACUUM_CLEANERS;
    case 'art_and_craft_supplies':
      return UPLOAD_SUBPATH_ART_AND_CRAFT_SUPPLIES;
    case 'bath_and_body':
      return UPLOAD_SUBPATH_BATH_AND_BODY;
    case 'beauty_tools':
      return UPLOAD_SUBPATH_BEAUTY_TOOLS;
    case 'hand_bags':
      return UPLOAD_SUBPATH_HAND_BAGS;
    case 'home_dining':
      return UPLOAD_SUBPATH_HOME_DINING;
    case 'single_board_computers':
      return UPLOAD_SUBPATH_SINGLE_BOARD_COMPUTERS;
    case 'wire_and_cable':
      return UPLOAD_SUBPATH_WIRE_AND_CABLE;
    case 'ethernet_switches':
      return UPLOAD_SUBPATH_ETHERNET_SWITCHES;
    case 'flashlights_and_work_lights':
      return UPLOAD_SUBPATH_FLASHLIGHTS_AND_WORK_LIGHTS;
    case 'batteries':
      return UPLOAD_SUBPATH_BATTERIES;
    default: {
      const _exhaustive: never = domain;
      return _exhaustive;
    }
  }
}

/**
 * Pick FSS upload_subpath so DESO can resolve dataset_mapping via ref_location.
 * Filename hints win. Returns null when the name does not match a known category
 * so FSS does not land unmatched files under home-kitchen.
 */
export function resolveUploadSubpath(options?: {
  fileName?: string;
  surfaceUkey?: string | null;
}): string | null {
  const fromFile = options?.fileName ? inferUploadDomainFromFileName(options.fileName) : null;
  if (fromFile) {
    return uploadSubpathForDomain(fromFile);
  }
  return null;
}

// ---------------------------------------------------------------------------
// File type constants
// ---------------------------------------------------------------------------

/** Allowed file extensions (with leading dot, lowercase). */
export const ALLOWED_EXTENSIONS: readonly string[] = [
  '.xlsx',
  '.xls',
  '.csv',
  '.tsv',
  '.zip',
];

/**
 * Deterministic extension → canonical MIME mapping (DR-10).
 * Used instead of File.type which is OS/browser-dependent for text formats.
 */
export const EXTENSION_TO_MIME_MAP: Readonly<Record<string, string>> = {
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.csv': 'text/csv',
  '.tsv': 'text/tab-separated-values',
  '.zip': 'application/zip',
};

/** Canonical MIME types — counterparts of ALLOWED_EXTENSIONS via EXTENSION_TO_MIME_MAP. */
export const ALLOWED_MIME_TYPES: readonly string[] = Object.values(EXTENSION_TO_MIME_MAP);

// ---------------------------------------------------------------------------
// Size / batch limits
// ---------------------------------------------------------------------------

/** 500 MB — matches FSS MAX_UPLOAD_SIZE_BYTES. */
export const MAX_FILE_SIZE_BYTES = 524_288_000;

/** UX-practical batch limit (FSS allows up to 50). */
export const MAX_FILES_PER_BATCH = 10;

// ---------------------------------------------------------------------------
// Attachment types
// ---------------------------------------------------------------------------

export type AttachmentStatus = 'pending' | 'creating' | 'uploading' | 'ready' | 'failed';

export interface PendingAttachment {
  /** Client-generated UUID (before FSS ukey is known). */
  localId: string;
  /** FSS stored-file ukey — null until creating → uploading transition. */
  ukey: string | null;
  fileName: string;
  /** Canonical MIME type (from EXTENSION_TO_MIME_MAP). */
  fileType: string;
  fileExtension: string;
  sizeBytes: number;
  status: AttachmentStatus;
  /** Upload percentage 0-100; meaningful only in uploading state. */
  progress: number;
  /** Error message on failure; null otherwise. */
  error: string | null;
  /** Browser File reference — kept for retry. */
  file: File;
  /** Active XHR reference — set during uploading, null otherwise. Used for abort on remove. */
  xhr: XMLHttpRequest | null;
}

export interface ConverseAttachment {
  /** FSS stored-file ukey. */
  file_id: string;
  file_name?: string;
  content_type?: string;
}

export interface FileValidationError {
  fileName: string;
  reason: 'invalid_type' | 'too_large' | 'batch_limit';
}

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

/**
 * Extract lowercase file extension including the dot.
 * Returns '' when the file name has no extension.
 * Handles dotfiles (e.g. '.gitignore') by returning the whole name.
 */
export function getExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return '';
  return fileName.slice(lastDot).toLowerCase();
}

/**
 * Look up the canonical MIME type for a file extension.
 * Returns undefined when the extension is not in the allowlist.
 */
export function getMimeFromExtension(ext: string): string | undefined {
  return EXTENSION_TO_MIME_MAP[ext.toLowerCase()];
}

/**
 * Validate a list of files against allowed extensions, size limit, and batch cap.
 *
 * @param files        Candidate File objects.
 * @param existingCount Number of attachments already in the store.
 * @returns { valid, rejected } — valid files proceed to upload; rejected files carry a reason.
 */
export function validateFiles(
  files: File[],
  existingCount: number,
): { valid: File[]; rejected: FileValidationError[] } {
  const total = existingCount + files.length;

  // Batch cap check — if the combined total exceeds the limit, reject everything in this batch.
  if (total > MAX_FILES_PER_BATCH) {
    return {
      valid: [],
      rejected: files.map(f => ({ fileName: f.name, reason: 'batch_limit' as const })),
    };
  }

  const valid: File[] = [];
  const rejected: FileValidationError[] = [];

  for (const file of files) {
    const ext = getExtension(file.name);
    const mime = getMimeFromExtension(ext);

    if (!mime) {
      rejected.push({ fileName: file.name, reason: 'invalid_type' });
      continue;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      rejected.push({ fileName: file.name, reason: 'too_large' });
      continue;
    }

    valid.push(file);
  }

  return { valid, rejected };
}
